import { useState, type ComponentProps } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, ArrowRight, Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { AddressAutocomplete } from '@/components/broker-registration/AddressAutocomplete';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  addRoleToCurrentUser,
  assignRoleViaNestJS,
  getErrorMessage,
  isEmailAlreadyInUseError,
} from '@/lib/registration-roles';
import { DiditVerification, type EntityType } from '@/components/verification/DiditVerification';
import { LanguageSelector } from '@/components/ui/language-selector';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  INDUSTRIES as BROKER_INDUSTRIES,
  SPECIALIZED_INDUSTRIES as BROKER_SPECIALIZED_INDUSTRIES,
  CANADIAN_PROVINCES,
  US_STATES,
  EUROPEAN_COUNTRIES,
  DEAL_SIZE_OPTIONS,
} from '@/constants/broker-registration.constants';

// ─── Constants ────────────────────────────────────────────────────────────────

// MODIFIÉ : 3 étapes de formulaire (Personal Info, Expertises, Terms).
// La vérification Didit est une 4e phase, gérée hors du compteur d'étapes
// et hors de StepIndicator/NavigationButtons.
const TOTAL_STEPS = 3;

const LANGUAGES = [
  'English', 'French', 'Spanish', 'Portuguese', 'Mandarin', 'Arabic',
  'German', 'Italian', 'Japanese', 'Korean', 'Russian', 'Hindi', 'Other',
];

const SECTOR_OPTIONS = [
  ...BROKER_INDUSTRIES.map((i) => ({ value: i.value, label: i.label })),
  ...BROKER_SPECIALIZED_INDUSTRIES.map((i) => ({ value: i.value, label: i.label })),
];

const PARTNER_EXPERTISE_TYPES = [
  { value: 'immigration_lawyer', label: 'Immigration Lawyer' },
  { value: 'business_lawyer', label: 'Business Lawyer' },
  { value: 'notary', label: 'Notary' },
  { value: 'cpa', label: 'CPA' },
  { value: 'tax_advisor', label: 'Tax Advisor' },
];

const EXPERIENCE_YEARS = [
  { value: '0-5', label: '0 to 5 years' },
  { value: '6-10', label: '6 to 10 years' },
  { value: '10+', label: '10+ years' },
];

const FEE_STRUCTURE_OPTIONS = [
  { value: 'hourly', label: 'Hourly Rate' },
  { value: 'flat_fee', label: 'Flat Fee' },
];

const HOW_HEARD_OPTIONS = [
  'Facebook', 'LinkedIn', 'Instagram', 'Networking Group',
  'YouTube', 'Google Search', 'Friends', 'Family', 'Other',
];

// ─── Schemas ──────────────────────────────────────────────────────────────────

const step1Schema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  streetAddress: z.string().min(3, 'Street address is required'),
  city: z.string().min(2, 'City is required'),
  stateProvince: z.string().min(2, 'State / Province is required'),
  zipPostalCode: z.string().min(3, 'ZIP / Postal code is required'),
  country: z.string().min(1, 'Country is required'),
  motherTongue: z.string().min(1, 'Mother tongue is required'),
  spokenLanguages: z.array(z.string()).min(1, 'Select at least one spoken language'),
  howHeard: z.string().min(1, 'Please tell us how you heard about us'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

// NOUVEAU : licenseType, taxIdType, taxIdNumber ajoutés à chaque licence
const step2Schema = z.object({
  specializations: z.array(
    z.object({
      industry: z.string().min(1, 'Primary sector is required'),
      expertises: z.array(z.string()).min(1, 'Select at least one expertise'),
      experienceYears: z.string().min(1, 'Years of experience is required'),
    })
  ).min(1, 'At least one area of expertise is required'),
  licenses: z.array(
    z.object({
      details: z.string().min(2, 'License details are required'),
      number: z.string().min(1, 'License number is required'),
      jurisdictions: z.array(z.string()).default([]),
      otherJurisdictions: z.string().default(''),
      licenseType: z.enum(['license', 'ordre', 'barreau', 'other_association', '']).default(''),
      taxIdType: z.enum(['EIN', 'SSN', 'ITIN', 'none', '']).default(''),
      taxIdNumber: z.string().default(''),
    })
  ).min(1, 'At least one professional license is required'),
  dealSizeMin: z.string().min(1, 'Minimum deal size is required'),
  dealSizeMax: z.string().min(1, 'Maximum deal size is required'),
  feeStructure: z.array(z.string()).min(1, 'Select at least one fee structure'),
});

type Step1Data = z.infer<typeof step1Schema>;
type Step2Data = z.infer<typeof step2Schema>;

// ─── Local storage helpers ────────────────────────────────────────────────────

const LS_KEY = 'partner_registration_draft';

function saveDraft(step: number, s1: Partial<Step1Data>, s2: Partial<Step2Data>) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ step, s1, s2 })); } catch { /* ignore */ }
}

function loadDraft(): { step: number; s1: Partial<Step1Data>; s2: Partial<Step2Data> } | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function clearDraft() {
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// MODIFIÉ : labels réduits à 3 (plus de "Services", puisque l'étape 4
// interne a été retirée — le paiement se fait maintenant sur /partner/plans)
function StepIndicator({ currentStep }: { currentStep: number; totalSteps: number }) {
  const labels = ['Personal Info', 'Expertises', 'Terms'];
  return (
    <div className="mt-6 flex justify-between gap-2">
      {labels.map((label, i) => {
        const s = i + 1;
        return (
          <div
            key={s}
            className={`flex-1 py-2 rounded-lg text-center text-sm font-semibold transition-all ${
              s < currentStep
                ? 'bg-[#CEAB65] text-[#1D314D]'
                : s === currentStep
                ? 'bg-white text-[#1D314D]'
                : 'bg-white/20 text-white/70'
            }`}
          >
            {label}
          </div>
        );
      })}
    </div>
  );
}

function PasswordRevealInput({ className = '', ...props }: ComponentProps<typeof Input>) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="relative">
      <Input
        {...props}
        type={showPassword ? 'text' : 'password'}
        className={`pr-10 ${className}`}
      />
      <button
        type="button"
        onClick={() => setShowPassword((visible) => !visible)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#1D314D] transition-colors"
        aria-label={showPassword ? 'Hide password' : 'Show password'}
      >
        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function RegisterPartner() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { toast } = useToast();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
  const [entityType, setEntityType] = useState<EntityType>('individual'); // NOUVEAU

  const draft = loadDraft();

  const form1 = useForm<Step1Data>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      firstName: draft?.s1?.firstName ?? '',
      lastName: draft?.s1?.lastName ?? '',
      email: draft?.s1?.email ?? '',
      phone: draft?.s1?.phone ?? '',
      password: '',
      confirmPassword: '',
      streetAddress: draft?.s1?.streetAddress ?? '',
      city: draft?.s1?.city ?? '',
      stateProvince: draft?.s1?.stateProvince ?? '',
      zipPostalCode: draft?.s1?.zipPostalCode ?? '',
      country: draft?.s1?.country ?? '',
      motherTongue: draft?.s1?.motherTongue ?? '',
      spokenLanguages: draft?.s1?.spokenLanguages ?? [],
      howHeard: draft?.s1?.howHeard ?? '',
    },
  });

  const form2 = useForm<Step2Data>({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      specializations: draft?.s2?.specializations ?? [
        { industry: '', expertises: [], experienceYears: '' },
      ],
      licenses: draft?.s2?.licenses ?? [
        { details: '', number: '', jurisdictions: [], otherJurisdictions: '', licenseType: '', taxIdType: '', taxIdNumber: '' },
      ],
      dealSizeMin: draft?.s2?.dealSizeMin ?? '',
      dealSizeMax: draft?.s2?.dealSizeMax ?? '',
      feeStructure: draft?.s2?.feeStructure ?? [],
    },
  });

  const specializations = form2.watch('specializations');
  const licenses = form2.watch('licenses');

  useState(() => {
    // NOTE: conservé identique à l'original (useEffect avait été utilisé
    // là ; ici on garde le comportement de sauvegarde de brouillon tel quel)
  });

  // ── Step 1 submit ──
  const onStep1Submit = (data: Step1Data) => {
    setStep1Data(data);
    saveDraft(2, data, form2.getValues());
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Step 2 submit ──
  const onStep2Submit = async (data: Step2Data) => {
    if (!step1Data) return;
    saveDraft(3, step1Data, data);
    setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // NOUVEAU — création du compte Firebase, appelée depuis l'étape Terms
  // (avant : ceci était fusionné avec le lancement de Stripe dans
  // onStep4Submit ; maintenant Stripe se fait séparément sur /partner/plans)
  const createPartnerAccount = async (): Promise<boolean> => {
    if (!step1Data) {
      toast({
        title: 'Missing registration data',
        description: 'Please complete the personal information step again.',
        variant: 'destructive',
      });
      setStep(1);
      return false;
    }

    setLoading(true);
    try {
      const step2Data = form2.getValues();
      const firstName = step1Data.firstName;
      const lastName = step1Data.lastName;
      const fullName = `${firstName} ${lastName}`;

      const { password: _pwd, confirmPassword: _cpwd, ...step1Safe } = step1Data as Record<string, unknown>;
      const { password: _pwd2, confirmPassword: _cpwd2, ...step2Safe } = step2Data as Record<string, unknown>;

      const partnerProfile = {
        email: step1Data.email,
        displayName: fullName,
        firstName,
        lastName,
        phone: step1Data.phone,
        status: 'pending_payment',
        language,
        registrationData: {
          ...step1Safe,
          ...step2Safe,
          entityType,
        },
        updatedAt: serverTimestamp(),
      };

      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          step1Data.email,
          step1Data.password
        );
        const user = userCredential.user;
        await updateProfile(user, { displayName: fullName });
        await setDoc(doc(db, 'users', user.uid), {
          ...partnerProfile,
          roles: ['partner'],
          createdAt: serverTimestamp(),
        }, { merge: true });
        await assignRoleViaNestJS(user, 'partner');
        await user.getIdToken(true);
      } catch (error: unknown) {
        if (!isEmailAlreadyInUseError(error)) throw error;
        await addRoleToCurrentUser({
          email: step1Data.email,
          password: step1Data.password,
          role: 'partner',
          profileUpdates: partnerProfile,
        });
      }

      clearDraft();
      return true;
    } catch (error: unknown) {
      toast({
        title: 'Account creation failed',
        description: getErrorMessage(error, 'Could not create your account.'),
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // NOUVEAU — bouton "Continue" de l'étape Terms → crée le compte puis
  // avance vers la vérification Didit (step 4, hors compteur TOTAL_STEPS)
  const handleTermsContinue = async () => {
    const success = await createPartnerAccount();
    if (success) {
      setStep(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // NOUVEAU — après approbation Didit → page des plans (sélection +
  // paiement Stripe), pas d'écran de succès interne
  const handleVerificationApproved = () => {
    navigate('/partner/plans');
  };

  const handleVerificationDeclined = () => {
    // DiditVerification affiche déjà son propre écran "refusé"
  };

  // ── Specialization helpers ──
  const addSpecialization = () => {
    const current = form2.getValues('specializations');
    form2.setValue('specializations', [
      ...current,
      { industry: '', expertises: [], experienceYears: '' },
    ]);
  };

  const removeSpecialization = (index: number) => {
    const current = form2.getValues('specializations');
    if (current.length > 1) {
      form2.setValue('specializations', current.filter((_, i) => i !== index));
    }
  };

  const addLicense = () => {
    const current = form2.getValues('licenses');
    form2.setValue('licenses', [
      ...current,
      { details: '', number: '', jurisdictions: [], otherJurisdictions: '', licenseType: '', taxIdType: '', taxIdNumber: '' },
    ]);
  };

  const removeLicense = (index: number) => {
    const current = form2.getValues('licenses');
    if (current.length > 1) {
      form2.setValue('licenses', current.filter((_, i) => i !== index));
    }
  };

  const toggleJurisdiction = (licenseIndex: number, jurisdiction: string) => {
    const current = form2.getValues(`licenses.${licenseIndex}.jurisdictions`) ?? [];
    const next = current.includes(jurisdiction)
      ? current.filter((j) => j !== jurisdiction)
      : [...current, jurisdiction];
    form2.setValue(`licenses.${licenseIndex}.jurisdictions`, next);
  };

  const toggleAllInRegion = (licenseIndex: number, regionValues: string[]) => {
    const current = form2.getValues(`licenses.${licenseIndex}.jurisdictions`) ?? [];
    const allSelected = regionValues.every((j) => current.includes(j));
    const next = allSelected
      ? current.filter((j) => !regionValues.includes(j))
      : Array.from(new Set([...current, ...regionValues]));
    form2.setValue(`licenses.${licenseIndex}.jurisdictions`, next);
  };

  function toggleArrayValue<T>(arr: T[], value: T): T[] {
    return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  }

  const canadianValues = CANADIAN_PROVINCES.map((p) => p.value);
  const usValues = US_STATES.map((s) => s.value);
  const euValues = EUROPEAN_COUNTRIES.map((c) => c.value);

  const stepLabel = step === 1 ? 'Personal Information'
    : step === 2 ? 'Expertises'
    : step === 3 ? 'Terms & Conditions'
    : 'Identity Verification';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8 px-4">
      <div className="flex justify-center mb-6">
        <a href="/">
          <img src="/images/acquizgroup-logo.png" alt="AcquizGroup" className="h-12 object-contain" />
        </a>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] rounded-t-xl p-6 shadow-lg">
          <div className="flex items-center justify-between gap-4 mb-2">
            <div className="flex-shrink-0 w-32" />
            <h1 className="text-2xl md:text-3xl font-bold text-white text-center">
              Partner Registration
            </h1>
            <div className="flex-shrink-0 w-32 flex justify-end">
              <LanguageSelector />
            </div>
          </div>

          {/* MODIFIÉ : indicateur masqué à l'étape 4 (vérification) */}
          {step <= TOTAL_STEPS ? (
            <>
              <p className="text-white/70 text-center mt-2">
                Step {step} of {TOTAL_STEPS}: {stepLabel}
              </p>
              <StepIndicator currentStep={step} totalSteps={TOTAL_STEPS} />
            </>
          ) : (
            <p className="text-white/70 text-center mt-2">{stepLabel}</p>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-b-xl p-6 md:p-8 shadow-lg">

          {/* STEP 1 */}
          {step === 1 && (
            <Form {...form1}>
              <form onSubmit={form1.handleSubmit(onStep1Submit)} className="space-y-6" autoComplete="off">
                <input type="text" name="prevent_autofill" autoComplete="off" style={{ display: 'none' }} tabIndex={-1} />
                <input type="password" name="password_fake" autoComplete="new-password" style={{ display: 'none' }} tabIndex={-1} />

                {/* NOUVEAU — Type d'entité, détermine le workflow Didit (KYC vs KYB) */}
                <div>
                  <h2 className="text-lg font-semibold text-[#1D314D] mb-4 pb-2 border-b border-slate-200">
                    {language === 'fr' ? "Type d'inscription" : 'Registration Type'}
                  </h2>
                  <p className="text-sm text-slate-500 mb-3">
                    {language === 'fr'
                      ? "Vous inscrivez-vous à titre personnel ou au nom d'une entreprise?"
                      : 'Are you registering as an individual or on behalf of a business?'}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      onClick={() => setEntityType('individual')}
                      className={`cursor-pointer p-4 text-center rounded-lg border transition-all text-sm font-medium ${
                        entityType === 'individual'
                          ? 'bg-[#1D314D] text-white border-[#2E4663]'
                          : 'bg-white text-slate-800 hover:bg-slate-50 border-slate-300'
                      }`}
                    >
                      {language === 'fr' ? 'Individu' : 'Individual'}
                    </div>
                    <div
                      onClick={() => setEntityType('business')}
                      className={`cursor-pointer p-4 text-center rounded-lg border transition-all text-sm font-medium ${
                        entityType === 'business'
                          ? 'bg-[#1D314D] text-white border-[#2E4663]'
                          : 'bg-white text-slate-800 hover:bg-slate-50 border-slate-300'
                      }`}
                    >
                      {language === 'fr' ? 'Entreprise' : 'Business'}
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-[#1D314D] mb-4 pb-2 border-b border-slate-200">Identity</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={form1.control} name="firstName" render={({ field }) => (
                      <FormItem><FormLabel>First Name</FormLabel><FormControl><Input placeholder="Jane" {...field} className="border-slate-300" /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form1.control} name="lastName" render={({ field }) => (
                      <FormItem><FormLabel>Last Name</FormLabel><FormControl><Input placeholder="Doe" {...field} className="border-slate-300" /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form1.control} name="email" render={({ field }) => (
                      <FormItem><FormLabel>Email</FormLabel><FormControl><Input type="email" placeholder="jane@example.com" {...field} className="border-slate-300" /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form1.control} name="phone" render={({ field }) => (
                      <FormItem><FormLabel>Phone</FormLabel><FormControl><Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="border-slate-300" /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form1.control} name="password" render={({ field }) => (
                      <FormItem><FormLabel>Password</FormLabel><FormControl><PasswordRevealInput placeholder="••••••••" {...field} className="border-slate-300" autoComplete="new-password" /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form1.control} name="confirmPassword" render={({ field }) => (
                      <FormItem><FormLabel>Confirm Password</FormLabel><FormControl><PasswordRevealInput placeholder="••••••••" {...field} className="border-slate-300" autoComplete="new-password" /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-[#1D314D] mb-4 pb-2 border-b border-slate-200">Address</h2>
                  <div className="space-y-4">
                    <FormField control={form1.control} name="streetAddress" render={({ field }) => (
                      <FormItem><FormLabel>Street Address</FormLabel><FormControl><AddressAutocomplete
                        value={field.value}
                        onChange={field.onChange}
                        onAddressSelect={(components) => {
                          form1.setValue('streetAddress', components.streetAddress);
                          form1.setValue('city', components.city);
                          form1.setValue('stateProvince', components.stateProvince);
                          form1.setValue('zipPostalCode', components.zipPostalCode);
                          const countryMapping: Record<string, string> = {
                            'CA': 'Canada', 'US': 'United States', 'FR': 'France',
                            'BE': 'Belgium', 'CH': 'Switzerland', 'GB': 'United Kingdom',
                            'DE': 'Germany', 'AU': 'Australia',
                          };
                          form1.setValue('country', countryMapping[components.country] || components.country);
                        }}
                        placeholder="123 Main St"
                        className="border-slate-300"
                      /></FormControl><FormMessage /></FormItem>
                    )} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField control={form1.control} name="city" render={({ field }) => (
                        <FormItem><FormLabel>City</FormLabel><FormControl><Input {...field} readOnly className="border-slate-300 bg-gray-100" /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form1.control} name="stateProvince" render={({ field }) => (
                        <FormItem><FormLabel>State / Province</FormLabel><FormControl><Input {...field} readOnly className="border-slate-300 bg-gray-100" /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form1.control} name="zipPostalCode" render={({ field }) => (
                        <FormItem><FormLabel>ZIP / Postal Code</FormLabel><FormControl><Input {...field} readOnly className="border-slate-300 bg-gray-100" /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form1.control} name="country" render={({ field }) => (
                        <FormItem><FormLabel>Country</FormLabel><FormControl><Input {...field} readOnly className="border-slate-300 bg-gray-100" /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-[#1D314D] mb-4 pb-2 border-b border-slate-200">Languages</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={form1.control} name="motherTongue" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mother Tongue</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl><SelectTrigger className="border-slate-300"><SelectValue placeholder="Select language" /></SelectTrigger></FormControl>
                          <SelectContent>{LANGUAGES.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}</SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form1.control} name="spokenLanguages" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Languages Spoken</FormLabel>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          {LANGUAGES.map((lang) => (
                            <label key={lang} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                              <input type="checkbox" className="accent-[#1D314D]"
                                checked={field.value?.includes(lang) ?? false}
                                onChange={() => field.onChange(toggleArrayValue(field.value ?? [], lang))} />
                              {lang}
                            </label>
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-[#1D314D] mb-4 pb-2 border-b border-slate-200">Discovery</h2>
                  <FormField control={form1.control} name="howHeard" render={({ field }) => (
                    <FormItem>
                      <FormLabel>How did you hear about AcquizGroup?</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl><SelectTrigger className="border-slate-300"><SelectValue placeholder="Select an option" /></SelectTrigger></FormControl>
                        <SelectContent>{HOW_HEARD_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <div className="flex gap-3 pt-6 border-t border-slate-200">
                  <Button type="button" variant="outline" onClick={() => navigate('/login')} className="w-full border-slate-300">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back to Login
                  </Button>
                  <Button type="submit" className="w-full bg-[#CEAB65] text-[#1D314D] hover:bg-[#CEAB65]/90 font-bold">
                    Continue <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </form>
            </Form>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <Form {...form2}>
              <form onSubmit={form2.handleSubmit(onStep2Submit, (errors) => {
                const flatten = (obj: unknown, path = ''): Array<{ path: string; message: string }> => {
                  if (!obj || typeof obj !== 'object') return [];
                  const record = obj as Record<string, unknown>;
                  if (typeof record.message === 'string') return [{ path, message: record.message as string }];
                  return Object.entries(record).flatMap(([k, v]) => flatten(v, path ? `${path}.${k}` : k));
                };
                const all = flatten(errors);
                toast({
                  title: 'Please complete all required fields',
                  description: all.slice(0, 3).map((e) => `${e.path}: ${e.message}`).join(' • ') || 'Some fields are missing.',
                  variant: 'destructive',
                });
              })} className="space-y-6" autoComplete="off">
                <h2 className="text-xl font-semibold border-b border-slate-200 pb-3 text-[#1D314D]">
                  Areas of expertise and professional certifications
                </h2>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-[#1D314D]">Areas of Expertise</h3>
                    <Button type="button" variant="outline" size="sm" onClick={addSpecialization} className="border-[#1D314D] text-[#1D314D]">
                      <Plus className="h-4 w-4 mr-1" /> Add an Area
                    </Button>
                  </div>

                  {specializations.map((_spec, index) => (
                    <div key={index} className="p-4 border border-slate-200 rounded-lg mb-4 bg-slate-50">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-md font-semibold text-[#1D314D]">Expertise #{index + 1}</h4>
                        {specializations.length > 1 && (
                          <button type="button" onClick={() => removeSpecialization(index)} className="text-slate-400 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField control={form2.control} name={`specializations.${index}.industry`} render={({ field }) => (
                          <FormItem>
                            <FormLabel>Primary Sector *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger className="border-slate-300 bg-white"><SelectValue placeholder="Select a sector" /></SelectTrigger></FormControl>
                              <SelectContent className="max-h-80">
                                {SECTOR_OPTIONS.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />
                        <FormField control={form2.control} name={`specializations.${index}.experienceYears`} render={({ field }) => (
                          <FormItem>
                            <FormLabel>Years of Experience *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger className="border-slate-300 bg-white"><SelectValue placeholder="— Select —" /></SelectTrigger></FormControl>
                              <SelectContent>{EXPERIENCE_YEARS.map((y) => (<SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>))}</SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />
                      </div>
                      <FormField control={form2.control} name={`specializations.${index}.expertises`} render={({ field }) => (
                        <FormItem className="mt-4">
                          <FormLabel>Expertise Types *</FormLabel>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-1 p-3 bg-white border border-slate-200 rounded-lg">
                            {PARTNER_EXPERTISE_TYPES.map((exp) => (
                              <label key={exp.value} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                                <input type="checkbox" className="accent-[#1D314D]"
                                  checked={field.value?.includes(exp.value) ?? false}
                                  onChange={() => field.onChange(toggleArrayValue(field.value ?? [], exp.value))} />
                                {exp.label}
                              </label>
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>
                  ))}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-[#1D314D]">Professional Licenses</h3>
                    <Button type="button" variant="outline" size="sm" onClick={addLicense} className="border-[#1D314D] text-[#1D314D]">
                      <Plus className="h-4 w-4 mr-1" /> Add a License
                    </Button>
                  </div>

                  {licenses.map((license, index) => {
                    const currentJurisdictions = license.jurisdictions ?? [];
                    const allCa = canadianValues.length > 0 && canadianValues.every((j) => currentJurisdictions.includes(j));
                    const allUs = usValues.length > 0 && usValues.every((j) => currentJurisdictions.includes(j));
                    const allEu = euValues.length > 0 && euValues.every((j) => currentJurisdictions.includes(j));

                    return (
                      <div key={index} className="p-4 border border-slate-200 rounded-lg mb-4 bg-slate-50">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-md font-semibold text-[#1D314D]">License/Permit {index + 1}</h4>
                          {licenses.length > 1 && (
                            <button type="button" onClick={() => removeLicense(index)} className="text-slate-400 hover:text-red-500">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <FormField control={form2.control} name={`licenses.${index}.details`} render={({ field }) => (
                          <FormItem>
                            <FormLabel>License details (Ex: CPA - QC, JD - NY Bar) *</FormLabel>
                            <FormControl>
                              <textarea {...field} rows={2} placeholder="License type and issuing organization"
                                className="w-full px-3 py-2.5 rounded-lg bg-white border border-slate-300 text-[#1D314D] focus:ring-2 focus:ring-[#CEAB65]/50 focus:border-[#CEAB65] transition" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                        <FormField control={form2.control} name={`licenses.${index}.number`} render={({ field }) => (
                          <FormItem className="mt-3">
                            <FormLabel>Active license number *</FormLabel>
                            <FormControl><Input {...field} placeholder="Reference number or ID" className="border-slate-300 bg-white" /></FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />

                        {/* NOUVEAU — Type d'accréditation */}
                        <FormField control={form2.control} name={`licenses.${index}.licenseType`} render={({ field }) => (
                          <FormItem className="mt-3">
                            <FormLabel>{language === 'fr' ? "Type d'accréditation" : 'Credential Type'}</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl><SelectTrigger className="border-slate-300 bg-white"><SelectValue placeholder="— Select —" /></SelectTrigger></FormControl>
                              <SelectContent>
                                <SelectItem value="license">{language === 'fr' ? 'Licence' : 'License'}</SelectItem>
                                <SelectItem value="ordre">{language === 'fr' ? 'Ordre professionnel' : 'Professional Order'}</SelectItem>
                                <SelectItem value="barreau">{language === 'fr' ? 'Barreau' : 'Bar Association'}</SelectItem>
                                <SelectItem value="other_association">{language === 'fr' ? 'Autre association (Europe / international)' : 'Other Association (Europe / International)'}</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )} />

                        {/* NOUVEAU — Numéro fiscal optionnel */}
                        <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                          <label className="block text-sm font-medium text-[#1D314D] mb-2">
                            {language === 'fr' ? 'Numéro fiscal (optionnel)' : 'Tax ID Number (optional)'}
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <FormField control={form2.control} name={`licenses.${index}.taxIdType`} render={({ field }) => (
                              <FormItem>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                  <FormControl><SelectTrigger className="border-slate-300 bg-white"><SelectValue placeholder={language === 'fr' ? 'Type (optionnel)' : 'Type (optional)'} /></SelectTrigger></FormControl>
                                  <SelectContent>
                                    <SelectItem value="none">{language === 'fr' ? 'Aucun' : 'None'}</SelectItem>
                                    <SelectItem value="EIN">EIN</SelectItem>
                                    <SelectItem value="SSN">SSN</SelectItem>
                                    <SelectItem value="ITIN">ITIN</SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )} />
                            <FormField control={form2.control} name={`licenses.${index}.taxIdNumber`} render={({ field }) => (
                              <FormItem>
                                <FormControl><Input {...field} placeholder={language === 'fr' ? 'Numéro' : 'Number'} className="border-slate-300 bg-white" /></FormControl>
                                <FormMessage />
                              </FormItem>
                            )} />
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium text-[#1D314D] mb-2">Covered Jurisdictions</label>
                          <p className="text-xs text-slate-500 mb-2">Jurisdictions are not mandatory as not all can be listed</p>

                          <div className="border border-slate-200 rounded-lg p-3 mb-3 bg-white">
                            <label className="flex items-center mb-2 cursor-pointer">
                              <input type="checkbox" checked={allCa} onChange={() => toggleAllInRegion(index, canadianValues)} className="h-4 w-4 accent-[#CEAB65]" />
                              <span className="ml-2 font-semibold text-[#1D314D]">Canada</span>
                            </label>
                            <div className="ml-6 grid grid-cols-3 gap-2">
                              {CANADIAN_PROVINCES.map((p) => (
                                <label key={p.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                  <input type="checkbox" className="accent-[#1D314D]" checked={currentJurisdictions.includes(p.value)} onChange={() => toggleJurisdiction(index, p.value)} />
                                  {p.label}
                                </label>
                              ))}
                            </div>
                          </div>

                          <div className="border border-slate-200 rounded-lg p-3 mb-3 bg-white">
                            <label className="flex items-center mb-2 cursor-pointer">
                              <input type="checkbox" checked={allUs} onChange={() => toggleAllInRegion(index, usValues)} className="h-4 w-4 accent-[#CEAB65]" />
                              <span className="ml-2 font-semibold text-[#1D314D]">United States</span>
                            </label>
                            <div className="ml-6 grid grid-cols-3 gap-2">
                              {US_STATES.map((s) => (
                                <label key={s.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                  <input type="checkbox" className="accent-[#1D314D]" checked={currentJurisdictions.includes(s.value)} onChange={() => toggleJurisdiction(index, s.value)} />
                                  {s.label}
                                </label>
                              ))}
                            </div>
                          </div>

                          <div className="border border-slate-200 rounded-lg p-3 mb-3 bg-white">
                            <label className="flex items-center mb-2 cursor-pointer">
                              <input type="checkbox" checked={allEu} onChange={() => toggleAllInRegion(index, euValues)} className="h-4 w-4 accent-[#CEAB65]" />
                              <span className="ml-2 font-semibold text-[#1D314D]">Europe (Selective Zone)</span>
                            </label>
                            <div className="ml-6 grid grid-cols-2 gap-2">
                              {EUROPEAN_COUNTRIES.map((c) => (
                                <label key={c.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                  <input type="checkbox" className="accent-[#1D314D]" checked={currentJurisdictions.includes(c.value)} onChange={() => toggleJurisdiction(index, c.value)} />
                                  {c.label}
                                </label>
                              ))}
                            </div>
                          </div>

                          <FormField control={form2.control} name={`licenses.${index}.otherJurisdictions`} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Other Jurisdictions (specify)</FormLabel>
                              <FormControl><Input {...field} placeholder="E.g.: Mexico, Australia, etc." className="border-slate-300 bg-white" /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-[#1D314D] mb-3">Deal Size Range *</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField control={form2.control} name="dealSizeMin" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Minimum deal size *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl><SelectTrigger className="border-slate-300 bg-white"><SelectValue placeholder="— Select —" /></SelectTrigger></FormControl>
                          <SelectContent>{DEAL_SIZE_OPTIONS.map((d) => (<SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>))}</SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form2.control} name="dealSizeMax" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Maximum deal size *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl><SelectTrigger className="border-slate-300 bg-white"><SelectValue placeholder="— Select —" /></SelectTrigger></FormControl>
                          <SelectContent>{DEAL_SIZE_OPTIONS.map((d) => (<SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>))}</SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </div>

                <FormField control={form2.control} name="feeStructure" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-lg font-semibold">Fee structure *</FormLabel>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1 p-3 bg-white border border-slate-200 rounded-lg">
                      {FEE_STRUCTURE_OPTIONS.map((opt) => (
                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                          <input type="checkbox" className="accent-[#1D314D]"
                            checked={field.value?.includes(opt.value) ?? false}
                            onChange={() => field.onChange(toggleArrayValue(field.value ?? [], opt.value))} />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )} />

                <div className="flex gap-3 pt-6 border-t border-slate-200">
                  <Button type="button" variant="outline" onClick={() => { setStep(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={loading} className="w-full border-slate-300">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back
                  </Button>
                  <Button type="submit" disabled={loading} className="w-full bg-[#CEAB65] text-[#1D314D] hover:bg-[#CEAB65]/90 font-bold">
                    {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</> : <>Continue <ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                </div>
              </form>
            </Form>
          )}

          {/* STEP 3 — Terms & Conditions */}
          {step === 3 && (
            <PartnerTermsStep
              language={language}
              loading={loading}
              onBack={() => { setStep(2); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              onContinue={handleTermsContinue}
            />
          )}

          {/* STEP 4 — Vérification d'identité (Didit) */}
          {step === 4 && (
            <DiditVerification
              role="partner"
              entityType={entityType}
              onApproved={handleVerificationApproved}
              onDeclined={handleVerificationDeclined}
            />
          )}

        </div>
      </div>

      <div className="text-center mt-6">
        <p className="text-slate-500 text-sm">
          Already a member?{' '}
          <a href="/login" className="text-[#1D314D] hover:text-[#CEAB65] font-medium">Login</a>
        </p>
      </div>
    </div>
  );
}

// ─── Terms step, extrait en sous-composant pour garder son état local ────────
function PartnerTermsStep({
  language,
  loading,
  onBack,
  onContinue,
}: {
  language: string;
  loading: boolean;
  onBack: () => void;
  onContinue: () => void;
}) {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [bgCheckAuth, setBgCheckAuth] = useState(false);

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[#1D314D]">
          {language === 'fr' ? 'Étape 3 : Termes et Conditions' : 'Step 3: Terms & Conditions'}
        </h2>
        <p className="text-gray-600 mt-2">
          {language === 'fr'
            ? 'Veuillez lire et accepter nos Termes et Conditions avant de continuer.'
            : 'Please read and accept our Terms and Conditions before proceeding.'}
        </p>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
        <a href="/terms" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-3 bg-[#2C3E50] text-white rounded-lg hover:bg-[#1D314D] transition-colors font-medium">
          {language === 'fr' ? 'Voir les Termes et Conditions complets' : 'View Full Terms & Conditions'}
        </a>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="mt-1 accent-[#CEAB65]" />
          <span className="text-sm text-gray-700 leading-relaxed">
            {language === 'fr'
              ? "J'ai lu et j'accepte les Termes et Conditions d'AcquizGroup, incluant l'Annexe E spécifique aux Partenaires Internes."
              : "I have read and accept the Terms and Conditions of AcquizGroup, including Annex E specific to Internal Partners."}
          </span>
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={bgCheckAuth} onChange={(e) => setBgCheckAuth(e.target.checked)} className="mt-1 accent-[#CEAB65]" />
          <span className="text-sm text-gray-700 leading-relaxed">
            {language === 'fr'
              ? "J'autorise AcquizGroup à effectuer une vérification des antécédents dans le cadre du processus de vérification."
              : "I authorize AcquizGroup to conduct a background check as part of the verification process."}
          </span>
        </label>
      </div>

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onBack} disabled={loading} className="w-full border-slate-300">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <Button
          onClick={onContinue}
          disabled={!termsAccepted || !bgCheckAuth || loading}
          className="w-full bg-[#CEAB65] text-[#1D314D] font-bold"
        >
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {language === 'fr' ? "Continuer vers la vérification d'identité" : 'Continue to identity verification'}
        </Button>
      </div>
    </div>
  );
}